import { api, unwrap, baseURL } from './client';
import { readToken } from '../auth/storage';

/**
 * Document service — shared across Expense / Reimbursement / Procurement / Asset / Payroll.
 */

export async function listDocuments(entityType, entityId) {
  const res = await api.get(`/finance/documents/${entityType}/${entityId}`);
  return unwrap(res) || [];
}

export async function uploadDocument({ file, entityType, entityId, department }) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('entityType', entityType);
  fd.append('entityId', String(entityId));
  if (department) fd.append('department', department);
  const res = await api.post('/finance/documents/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

export async function saveDocumentMetadata(payload) {
  const res = await api.post('/finance/documents', payload);
  return unwrap(res);
}

export async function deleteDocument(id) {
  const res = await api.delete(`/finance/documents/${id}`);
  return unwrap(res);
}

/**
 * The download endpoint returns a binary stream. We construct the URL and
 * the consumer opens it in a new tab — the request interceptor cannot attach
 * Auth headers to a window.open() call, so we use a fetch-and-blob approach.
 */
export async function downloadDocumentBlob(id) {
  const res = await fetch(`${baseURL}/finance/documents/download/${id}`, {
    headers: { Authorization: `Bearer ${readToken()}` },
  });
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob, filename: match?.[1] || `document-${id}` };
}

export async function triggerDocumentDownload(doc) {
  const { blob, filename } = await downloadDocumentBlob(doc.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.fileName || filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetches the file for a document via the /view endpoint and returns a
 * blob object-URL suitable for embedding in an <iframe> (inline, not download).
 * Caller is responsible for revoking the URL when done.
 */
export async function viewDocumentBlobUrl(id) {
  const res = await fetch(`${baseURL}/finance/documents/view/${id}`, {
    headers: { Authorization: `Bearer ${readToken()}` },
  });
  if (!res.ok) {
    throw new Error(`Failed to load file (${res.status})`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
